/**
 * Assembles the {system, messages} payload for one Coach Brain turn.
 *
 * Pulls:
 *  - system prompt (identity + rules + tool catalogue + screen context)
 *  - last N conversation turns from coach_messages
 *  - the new user query
 *
 * Phase 3 will extend this to prepend retrieved memories.
 */
import { supabase } from '@4cm/core/supabase';
import { buildBrainSystemPrompt, type ScreenContext, type CoachProfileBlock } from './brainPrompt';
import { searchMemories, screenContextToTags } from './brainMemory';
import type { AIMessage } from '@4cm/core/ai';

export interface BuildContextArgs {
  coachId: string;
  teamId: string | null;
  screenContext: ScreenContext;
  conversationId: string | null;
  query: string;
  historyLimit?: number;
}

export interface BrainContext {
  system: string;
  messages: AIMessage[];
}

/** Rough minimum roster per game format — starters + a sensible sub buffer. */
const ROSTER_TARGETS: Record<string, { starters: number; target: number }> = {
  '5v5':   { starters: 5,  target: 8 },
  '7v7':   { starters: 7,  target: 10 },
  '9v9':   { starters: 9,  target: 14 },
  '11v11': { starters: 11, target: 16 },
};

/** FA 4 corner model phase labels stored in principles.type. */
const PHASES = ['ATTACK', 'DEFEND', 'TRANSITION', 'RESTART'] as const;
/** Target number of principles per phase — small-but-not-sparse. */
const PRINCIPLES_PER_PHASE_TARGET = 2;

/** Fetch a rich completeness picture for the active team. Reports both
 *  progress ("3 of 5 phases covered") and specific gaps so the Brain can
 *  either fill them or ask the coach a clarifying question. */
async function fetchWorkflowState(teamId: string | null): Promise<string> {
  if (!teamId) {
    return [
      '<workflow_state>',
      '- step 1 team: INCOMPLETE',
      '- step 2 roster: INCOMPLETE',
      '- step 3 principles: INCOMPLETE',
      '- step 4 curriculum: INCOMPLETE',
      '- step 5 calendar: INCOMPLETE',
      '',
      'The coach has NO team yet. If they ask for anything that depends on',
      'a team (principles, sessions, curriculum, roster), first suggest they',
      'complete Team Setup (step 1 in the sidebar).',
      '</workflow_state>',
    ].join('\n');
  }

  // Pull everything we need in a small batch. A missing team row is rare
  // (teamId passed in usually came from App.tsx which set it after load) but
  // treat it as an error state so the Brain can prompt a full redo.
  const [teamRes, playersRes, principlesRes, curriculumRes, sessionsRes] = await Promise.all([
    supabase.from('teams').select('name, age_group, formation_default, player_count, training_days_per_week, session_duration_mins, season_start, season_end').eq('id', teamId).maybeSingle(),
    supabase.from('team_players').select('id', { count: 'exact', head: true }).eq('team_id', teamId),
    supabase.from('principles').select('type, corner_ids').eq('team_id', teamId),
    supabase.from('curriculum_plans').select('plan_data').eq('team_id', teamId).eq('is_active', true).maybeSingle(),
    supabase.from('sessions').select('id', { count: 'exact', head: true }).eq('team_id', teamId),
  ]);

  const team       = teamRes.data as any | null;
  const rosterN    = playersRes.count ?? 0;
  const principles = (principlesRes.data ?? []) as Array<{ type: string; corner_ids: string[] | null }>;
  const curriculum = curriculumRes.data as any | null;
  const sessionN   = sessionsRes.count ?? 0;

  // ---------- step 1 team ----------
  const teamGaps: string[] = [];
  if (!team) teamGaps.push('team row missing');
  else {
    if (!team.name) teamGaps.push('name not set');
    if (!team.age_group) teamGaps.push('age group not set');
    if (!team.formation_default) teamGaps.push('default formation not set');
    if (!team.season_start || !team.season_end) teamGaps.push('season dates not set');
    if (!(team.player_count > 0)) teamGaps.push('player count not set');
    if (!(team.training_days_per_week > 0)) teamGaps.push('training days/week not set');
    if (!(team.session_duration_mins > 0)) teamGaps.push('session duration not set');
  }
  const teamLine = team && teamGaps.length === 0
    ? `COMPLETE (${team.name}, ${team.age_group}, ${team.formation_default})`
    : `INCOMPLETE — ${teamGaps.join(', ')}`;

  // ---------- step 2 roster ----------
  // Infer format from the age group. (AGE_GROUPS is the authoritative mapping
  // but we only need the format string here; derive it from the formation
  // length or the age code to avoid pulling the constants module into this
  // server-side helper.)
  const format = inferFormat(team);
  const rosterTarget = ROSTER_TARGETS[format] ?? { starters: 0, target: 0 };
  const rosterLine = rosterN >= rosterTarget.target
    ? `COMPLETE (${rosterN} players, ${format})`
    : rosterN >= rosterTarget.starters
      ? `PARTIAL (${rosterN}/${rosterTarget.target} for ${format} — minimally playable but a deeper bench helps)`
      : `INCOMPLETE (${rosterN}/${rosterTarget.target} for ${format} — need at least ${rosterTarget.starters} starters)`;

  // ---------- step 3 principles ----------
  const perPhase: Record<string, number> = { ATTACK: 0, DEFEND: 0, TRANSITION: 0, RESTART: 0 };
  let hasPsychOrSocial = false;
  for (const p of principles) {
    const t = String(p.type ?? '').toUpperCase();
    if (t in perPhase) perPhase[t] += 1;
    const corners = (p.corner_ids ?? []).map((s) => String(s));
    if (corners.some((c) => /psych|social/i.test(c))) hasPsychOrSocial = true;
  }
  const thinPhases = PHASES.filter((p) => perPhase[p] < PRINCIPLES_PER_PHASE_TARGET);
  const principleGaps: string[] = [];
  if (thinPhases.length > 0) {
    principleGaps.push(
      'thin phases: ' + thinPhases.map((p) => `${p} has ${perPhase[p]} (target ${PRINCIPLES_PER_PHASE_TARGET})`).join(', '),
    );
  }
  if (principles.length > 0 && !hasPsychOrSocial) {
    principleGaps.push('no principle tags Psychological or Social corner (spec §04.1 requires ≥1)');
  }
  const principleLine = principles.length === 0
    ? 'INCOMPLETE — no principles drafted yet (target: ~2 per phase across ATTACK/DEFEND/TRANSITION/RESTART, ≥1 with psych/social corner)'
    : principleGaps.length === 0
      ? `COMPLETE (${principles.length} principles, all phases covered, psych/social tag present)`
      : `PARTIAL (${principles.length} principles) — ${principleGaps.join('; ')}`;

  // ---------- step 4 curriculum ----------
  const curriculumGaps: string[] = [];
  let curriculumDesc = '';
  if (!curriculum) {
    curriculumGaps.push('no active plan');
  } else {
    const plan = curriculum.plan_data ?? {};
    const totalWeeks = plan.total_weeks ?? (plan.phase_plans ?? []).reduce((s: number, ph: any) => s + (ph.weeks ?? 0), 0);
    if (!totalWeeks) curriculumGaps.push('plan has no weeks');
    else {
      const expected = team?.season_start && team?.season_end
        ? Math.max(1, Math.round((Date.parse(team.season_end) - Date.parse(team.season_start)) / (7 * 86400_000)))
        : null;
      if (expected && totalWeeks < expected - 1) curriculumGaps.push(`plan covers ${totalWeeks} weeks but season spans ~${expected}`);
      curriculumDesc = `${totalWeeks} weeks across ${(plan.phase_plans ?? []).length} phases`;
    }
  }
  const curriculumLine = curriculum && curriculumGaps.length === 0
    ? `COMPLETE (${curriculumDesc})`
    : curriculum
      ? `PARTIAL (${curriculumDesc}) — ${curriculumGaps.join('; ')}`
      : 'INCOMPLETE — no curriculum plan';

  // ---------- step 5 calendar ----------
  const calendarLine = sessionN > 0 ? `has ${sessionN} session(s)` : 'no sessions yet';

  const lines = [
    '<workflow_state>',
    `- step 1 team: ${teamLine}`,
    `- step 2 roster: ${rosterLine}`,
    `- step 3 principles: ${principleLine}`,
    `- step 4 curriculum: ${curriculumLine}`,
    `- step 5 calendar: ${calendarLine}`,
    '',
    'Completeness targets:',
    '- team: name, age_group, formation, season dates, training frequency',
    '- roster: enough players for the game format with a sub buffer',
    '- principles: ~2 per phase (ATTACK/DEFEND/TRANSITION/RESTART), ≥1 tagged psych or social',
    '- curriculum: active plan whose weeks span the season dates',
    '',
    'When a step is PARTIAL or INCOMPLETE, identify the SPECIFIC missing pieces',
    'and either: (a) draft the missing items directly if you have enough context,',
    'or (b) ask the coach ONE precise clarifying question at a time for the gap',
    '(e.g. "You have no DEFEND principles yet — how aggressive do you want the',
    'press in your own half?"). Never ask vague questions when the gap is known.',
    'After a step is COMPLETE, suggest the next INCOMPLETE step by name.',
    '</workflow_state>',
  ];
  return lines.join('\n');
}

/** Best-effort mapping of a team record to its game format. */
function inferFormat(team: any | null): string {
  if (!team) return '11v11';
  // AGE_GROUPS format table (kept local to avoid dragging the client constants
  // module into this server-ish helper).
  const byAge: Record<string, string> = {
    'U7-U8':   '5v5',
    'U9-U10':  '7v7',
    'U11-U12': '9v9',
    'U13-U14': '11v11',
    'U15-U16': '11v11',
    'U17-U18': '11v11',
    'U19+':    '11v11',
  };
  return byAge[team.age_group] ?? '11v11';
}

async function fetchCoachProfile(coachId: string): Promise<CoachProfileBlock | null> {
  const { data } = await supabase.from('users')
    .select('coach_level, years_coaching, age_groups_coached, coach_badges, coach_methodology, coach_comms_style')
    .eq('id', coachId)
    .maybeSingle();
  if (!data) return null;
  // Derive effective comms style from level if the coach hasn't set one
  // explicitly — mirrors defaultCommsFor in packages/core/profiles.ts.
  // Duplicated here to avoid a cross-package import cycle; keep in sync.
  const level = data.coach_level as string | null;
  const comms = (data.coach_comms_style as 'concise' | 'standard' | 'scaffolded' | null)
    ?? (level === 'ADVANCED' || level === 'ELITE_CAT1' ? 'concise'
        : level === 'NOVICE' || level === 'GRASSROOTS' ? 'scaffolded'
        : 'standard');
  return {
    level,
    years_coaching: data.years_coaching as number | null,
    age_groups: (data.age_groups_coached as string[] | null) ?? [],
    badges: (data.coach_badges as string[] | null) ?? [],
    methodology: (data.coach_methodology as string | null) ?? null,
    comms_style: comms,
  };
}

export async function buildBrainContext(args: BuildContextArgs): Promise<BrainContext> {
  const { coachId, teamId, screenContext, conversationId, query } = args;
  const historyLimit = args.historyLimit ?? 20;

  // Retrieve top memories relevant to this turn.
  const tags = screenContextToTags({
    kind: screenContext.kind,
    teamId: screenContext.teamId ?? teamId,
    sessionId: screenContext.sessionId,
    playerId: screenContext.playerId,
  });
  const [memories, coachProfile] = await Promise.all([
    searchMemories({
      coach_id: coachId,
      team_id: teamId,
      query,
      tags,
      limit: 5,
    }),
    fetchCoachProfile(coachId),
  ]);

  const memoryBlock = memories.length
    ? '<relevant_memories>\n' +
      memories.map(m =>
        `- [${m.scope}${m.entity_id ? `:${m.entity_id.slice(0, 8)}` : ''} · ${m.kind}] ${m.fact}` +
        (m.tags?.length ? ` (${m.tags.join(', ')})` : '')
      ).join('\n') +
      '\n</relevant_memories>'
    : '';

  const workflowBlock = await fetchWorkflowState(teamId);

  const system = buildBrainSystemPrompt({
    coachId,
    teamId,
    screenContext,
    coachProfile,
    extraBlocks: [
      workflowBlock,
      ...(memoryBlock ? [memoryBlock] : []),
    ],
  });

  const messages: AIMessage[] = [];

  if (conversationId) {
    const { data } = await supabase.from('coach_messages')
      .select('role, content, tool_calls, draft_payload')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .limit(historyLimit);

    for (const row of data ?? []) {
      // Only 'user' and 'assistant' prose belong in the conversation replay.
      // Tool rounds from prior turns are summarised inside the assistant content.
      if (row.role === 'user') {
        messages.push({ role: 'user', content: row.content });
      } else if (row.role === 'assistant') {
        // Prefer the saved prose; if empty, fall back to a short tool-call note.
        const text = row.content?.trim()
          ? row.content
          : (Array.isArray(row.tool_calls) && row.tool_calls.length
              ? `(consulted: ${row.tool_calls.map((t: any) => t.name).join(', ')})`
              : '(no response)');
        messages.push({ role: 'assistant', content: text });
      }
    }
  }

  messages.push({ role: 'user', content: query });

  return { system, messages };
}
